import { motion } from "framer-motion";
import { Check, ArrowUpRight, Clock, Smartphone, LifeBuoy } from "lucide-react";
import { scrollToId } from "@/config";

const tiers = [
  {
    id: "starter",
    name: "Starter Local",
    tagline: "Per farsi trovare in città",
    features: ["Sito vetrina one-page", "Google Maps & Local SEO", "Modulo contatti + WhatsApp", "Dominio e hosting guidati"],
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro Business",
    tagline: "Per attività e professionisti",
    features: ["Sito multi-pagina su misura", "Prenotazioni o menu digitale", "Blog e area news", "Analytics e report mensili"],
    highlight: true,
  },
  {
    id: "ecommerce",
    name: "E-commerce Custom",
    tagline: "Per vendere online, 24/7",
    features: ["Catalogo prodotti completo", "Pagamenti integrati", "Gestione ordini semplice", "Ottimizzazione conversioni"],
    highlight: false,
  },
];

const guarantees = [
  { icon: Clock, label: "Consegna in 5–10 giorni" },
  { icon: Smartphone, label: "100% Mobile Friendly" },
  { icon: LifeBuoy, label: "Assistenza inclusa" },
];

export const Pricing = () => {
  return (
    <section id="pacchetti" className="relative py-24 lg:py-32" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">Investimento</p>
          <h2 className="font-syne text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Niente listini rigidi.{" "}
            <span className="text-zinc-500">Solo preventivi su misura.</span>
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Ogni attività è diversa: raccontami la tua e ricevi un preventivo chiaro, senza sorprese e senza impegno.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`relative flex flex-col rounded-2xl border p-8 transition-all duration-300 ${
                t.highlight
                  ? "border-cyan-400/40 bg-zinc-900/80 shadow-[0_0_50px_rgba(56,189,248,0.1)]"
                  : "border-white/10 bg-zinc-900/60 hover:border-white/20"
              }`}
              data-testid={`pricing-tier-${t.id}`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-8 rounded-full bg-cyan-400 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-950 font-medium">
                  Più richiesto
                </span>
              )}
              <h3 className="font-syne text-xl font-bold">{t.name}</h3>
              <p className="mt-1 text-sm text-zinc-500">{t.tagline}</p>
              <p className="mt-6 font-syne text-2xl font-extrabold text-cyan-400">Preventivo su richiesta</p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                    <Check size={16} className="mt-0.5 shrink-0 text-cyan-400" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToId("#contatti")}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all duration-300 ${
                  t.highlight
                    ? "bg-cyan-400 text-zinc-950 hover:bg-cyan-300 hover:shadow-[0_0_24px_rgba(56,189,248,0.4)]"
                    : "border border-white/15 text-zinc-100 hover:border-cyan-400/50 hover:text-cyan-300"
                }`}
                data-testid={`pricing-cta-${t.id}`}
              >
                Chiedi un preventivo
                <ArrowUpRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-12 flex flex-wrap justify-center gap-4"
        >
          {guarantees.map((g) => (
            <span
              key={g.label}
              className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-zinc-900/60 px-5 py-2.5 text-sm text-zinc-300"
            >
              <g.icon size={16} className="text-amber-400" />
              {g.label}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
